import * as React from 'react';
import isEqual from 'react-fast-compare';
import debounce from 'lodash-es/debounce';
import { AutoSuggestService } from 'services';
import { AutoSuggestItem, AutoSuggestParams } from './Types';
import AutoSuggestContext, { AutoSuggestContextValue } from './AutoSuggestContext';

export interface AutoSuggestDataState {
	results: any[];
	isLoading: boolean;
	errorStatus: number | null;
	hasMore: boolean;
}

export interface AutoSuggestDataParams {
	selectedItems?: AutoSuggestItem[];
}

export type AutoSuggestDataRenderProps = AutoSuggestDataState & {
	createValue: (inputValue) => Promise<any>;
};

export interface AutoSuggestDataProps {
	selectedItems?: AutoSuggestItem[];
	query: string | null;
	children: (props: AutoSuggestDataRenderProps) => React.ReactNode;
	params?: AutoSuggestParams;
}

/**
 * Component responsible for loading AutoSuggest data from the provided endpoint.
 *
 * @class AutoSuggestData
 * @extends {React.Component<AutoSuggestDataProps, AutoSuggestDataState>}
 */
class AutoSuggestData extends React.Component<AutoSuggestDataProps, AutoSuggestDataState> {
	public static contextType: React.Context<any> = AutoSuggestContext;
	public declare context: AutoSuggestContextValue;
	public mounted: boolean = false;

	constructor(props) {
		super(props);

		this.state = {
			results: [],
			isLoading: false,
			errorStatus: null,
			hasMore: false,
		};

		this.getResultsDebounced = debounce(this.getResults, 200);
	}

	/**
	 * Refresh on mount.
	 *
	 * @memberof AutoSuggestData
	 */
	public componentDidMount(): void {
		this.mounted = true;

		/*
         we conditionally render this component when downshift isOpen = true
		 that means that this'll run when the autosuggest dropdown first shows up.
		 this could be from focusing and hitting the down arrow or just clicking
         the toggle button.
         */
		this.refreshDataDebounced();
	}

	public componentWillUnmount() {
		this.mounted = false;
	}

	/**
	 * This lifecycle function is called after a component is done updating.
	 * For us, this is when the parent changes (typed something into the input)
	 * or when isOpen becomes true, which causes this component to render.
	 * @param param0
	 * @param prevState
	 */
	// eslint-disable-next-line no-unused-vars
	public componentDidUpdate({ children: _, ...prevProps }): void {
		// eslint-disable-next-line no-unused-vars
		const { children, ...props } = this.props;

		/*
		 important to check props to not change state every time as this can
		 cause an endless loop (state causes update, which updates state, ...)
		 the default value for data is undefined. we set it to [] here so that
		 we know that this isn't the first time we call. this is useful because
		 prevProps and props will match on first load, which means we can't
         do a query if there's no value entered in the input.
        */
		if (!isEqual(prevProps, props) || !this.state.results) {
			// eslint-disable-next-line
			this.setState({ results: [] });
			this.refreshDataDebounced();
		}
	}

	private getResultsDebounced: any = null;

	/**
	 * Queues a data refresh, which will be debounced
	 */
	private refreshDataDebounced = (): void => {
		this.setState({ isLoading: true });
		this.getResultsDebounced();
	};

	/**
	 * Creates a new value on the server
	 * The type here is `any` because it may be customized via `createNewValueAutoSuggestObject`
	 */
	private createValue = async (inputValue): Promise<any> => {
		console.log('Creating value on server:', inputValue);

		let newValue: any = null;

		const { autoSuggestOptions } = this.context;

		if (!autoSuggestOptions.baseUrl) {
			// this should never happen as we assert auto suggest options at the top level components.
			return;
		}

		// allow consumers to define the default object by passing a function
		if (typeof autoSuggestOptions.createNewValueObject === 'function') {
			newValue = autoSuggestOptions.createNewValueObject(inputValue);
		} else {
			// if they haven't provided a function, let's just create it with a name field and any params.
			newValue = { name: inputValue, ...autoSuggestOptions.params };
		}

		const { entity } = await new AutoSuggestService(
			autoSuggestOptions.baseUrl,
			autoSuggestOptions.suggestSlugOverride,
		).createFromUserInput(newValue);

		if (!this.mounted) {
			return;
		}

		console.debug('Created value:', entity);

		let newAutoSuggestValue: any = null;

		// when the value comes back from the server, it may not have name/id fields.
		// in that case, consumers must pass a function to map this to a new object.
		if (typeof autoSuggestOptions.createNewValueAutoSuggestObject === 'function') {
			newAutoSuggestValue = autoSuggestOptions.createNewValueAutoSuggestObject(entity);
		} else {
			// by default, assume name/id are present. rename them to label/value respectively.
			const { name: label, id: value, ...rest } = entity;
			newAutoSuggestValue = { label, value, ...rest };
		}

		return newAutoSuggestValue;
	};

	/**
	 * Loads results from the AutoSuggest endpoint specified in props.
	 */
	private getResults = async (): Promise<void> => {
		if (!this.mounted) {
			return;
		}

		this.setState({
			isLoading: true,
			errorStatus: null,
		});

		console.debug('Loading AutoSuggest Data...');

		const {
			autoSuggestOptions: { baseUrl, suggestSlugOverride },
		} = this.context;

		if (!baseUrl) {
			// this should never happen as we assert auto suggest options at the top level components.
			return;
		}

		const { selectedItems, query, params = {} } = this.props;

		// get list of unique values to ignore from autosuggest, to avoid getting duplicates.
		const selectedItemIds = selectedItems && selectedItems.map((item) => item.value.toString());
		const ids: string[] = !Array.isArray(params.ignoredIds) ? [] : params.ignoredIds;

		if (selectedItemIds) {
			selectedItemIds.filter((id) => !ids.find((i) => i === id)).forEach((id) => ids.push(id));
		}
		params.ignoredIds = ids;

		try {
			const options = await new AutoSuggestService(baseUrl, suggestSlugOverride).getOptions(query || '', params);

			if (!this.mounted) {
				return;
			}

			this.setState({
				results: options.records,
				hasMore: options.hasMore,
				isLoading: false,
				errorStatus: null,
			});
		} catch (error) {
			if (!this.mounted) {
				return;
			}

			this.setState({
				results: [],
				isLoading: false,
				errorStatus: error.response.status,
			});
		}
	};

	public render() {
		const { children } = this.props;
		const { results, isLoading, errorStatus, hasMore } = this.state;

		return children({
			results,
			isLoading,
			errorStatus,
			hasMore,
			createValue: this.createValue,
		});
	}
}

export default AutoSuggestData;
